import React, { useEffect, useRef, useState } from "react";

/**
 * VideoPlayer component
 * @param {Object} props
 * @param {string} props.videoSrc
 * @param {boolean} props.videoCompleted - Whether video is completed
 * @param {function} props.onVideoComplete - Function to mark video as completed
 */
export default function VideoPlayer({
  videoSrc,
  videoCompleted,
  onVideoComplete,
  onVideoEnd,
}) {
  const videoPlayerRef = useRef(null);
  const progressRef = useRef(null);
  const ProgressContainerRef = useRef(null);
  const dragRef = useRef(null);
  const [isVideoPlayed, setIsVideoPlayed] = useState(false);
  const [isVideoMuted, setIsVideoMuted] = useState(false);
  const [isSettingShown, setIsSettingShown] = useState(false);
  const [isMiniPlayer, setIsMiniPlayer] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [totalTime, setTotalTime] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const clickTimeoutRef = useRef(null);
  const [position, setPosition] = useState({
    x: window.innerWidth - 340,
    y: window.innerHeight - 200,
  });
  const mouseMoved = useRef(false);
  const hasCalledOnVideoEndRef = useRef(false);

  const handlePlayClick = () => {
    setIsVideoPlayed((prev) => !prev);
  };

  const handleVideoClick = () => {
    if (isDragging || mouseMoved.current) return;

    if (clickTimeoutRef.current === null) {
      clickTimeoutRef.current = setTimeout(() => {
        handlePlayClick();
        clickTimeoutRef.current = null;
      }, 200);
    }
  };

  const handleVideoDoubleClick = (e) => {
    if (isDragging || mouseMoved.current) return;

    if (clickTimeoutRef.current !== null) {
      clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;
    }



    if (videoPlayerRef.current) {
      const videoRect = videoPlayerRef.current.getBoundingClientRect();
      const clickPosition = e.clientX - videoRect.left;
      const middle = videoRect.width / 2;

      if (clickPosition < middle) {
        videoPlayerRef.current.currentTime -= 10;
      } else {
        videoPlayerRef.current.currentTime += 10;
      }
    }
  };

  const handleMuteClick = () => {
    setIsVideoMuted((prev) => !prev);
  };

  const handleTimeUpdate = () => {
    if (videoPlayerRef.current) {
      const updatedCurrentTime = videoPlayerRef.current.currentTime;
      setCurrentTime(updatedCurrentTime);

      const progressPercentage = (updatedCurrentTime / totalTime) * 100;
      progressRef.current.style.width = `${progressPercentage}%`;

      // Check if 90% reached and call onVideoEnd once
      if (
        totalTime > 0 &&
        updatedCurrentTime / totalTime >= 0.9 &&
        !hasCalledOnVideoEndRef.current
      ) {
        hasCalledOnVideoEndRef.current = true;
        if (onVideoEnd) onVideoEnd();
      }
    }
  };

  const handleLoadedMetadata = () => {
    if (videoPlayerRef.current) {
      setTotalTime(videoPlayerRef.current.duration);
    }
  };

  const handleForwardClick = () => {
    if (videoPlayerRef.current) {
      videoPlayerRef.current.currentTime += 10;
    }
  };

  const handleBackwardClick = () => {
    if (videoPlayerRef.current) {
      videoPlayerRef.current.currentTime -= 10;
    }
  };

  const handleSettingClick = () => {
    setIsSettingShown((prev) => !prev);
  };

  const handlePlaybackSpeedChange = (e) => {
    if (videoPlayerRef.current) {
      videoPlayerRef.current.playbackRate = parseFloat(e.target.value);
    }
  };

  const handleProgressBarClick = (e) => {
    if (videoPlayerRef.current) {
      const progressBar = ProgressContainerRef.current;
      const clickPosition = e.nativeEvent.offsetX;
      const progressBarWidth = progressBar.offsetWidth;
      const newTime = (clickPosition / progressBarWidth) * totalTime;

      videoPlayerRef.current.currentTime = newTime;
    }
  };

  const handleFullScreenClick = () => {
    const container = dragRef.current;

    if (
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement
    ) {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    } else {
      // Enter fullscreen
      if (container.requestFullscreen) {
        container.requestFullscreen();
      } else if (container.webkitRequestFullscreen) {
        container.webkitRequestFullscreen();
      } else if (container.mozRequestFullScreen) {
        container.mozRequestFullScreen();
      } else if (container.msRequestFullscreen) {
        container.msRequestFullscreen();
      }
    }
  };

  const handleMiniPlayerClick = (e) => {
    setIsMiniPlayer((prev) => !prev);
  };

  const handleMouseDown = (e) => {
    if (!isMiniPlayer) return;

    e.preventDefault();
    setIsDragging(true);
    mouseMoved.current = false;

    const startX = e.clientX - position.x;
    const startY = e.clientY - position.y;
    const initialPosition = { x: position.x, y: position.y };

    const handleMouseMove = (e) => {
      const newPosition = {
        x: e.clientX - startX,
        y: e.clientY - startY,
      };

      if (
        newPosition.x !== initialPosition.x ||
        newPosition.y !== initialPosition.y
      ) {
        mouseMoved.current = true;
      }

      setPosition(newPosition);
    };

    const handleMouseUp = () => {
      setIsDragging(false);

      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);

      setTimeout(() => {
        mouseMoved.current = false;
      }, 50);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  useEffect(() => {
    if (videoPlayerRef.current) {
      const video = videoPlayerRef.current;

      if (isVideoPlayed) {
        // Handle play() promise to avoid race condition errors
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.catch((error) => {
            // Handle play() interruption gracefully

          });
        }
      } else {
        video.pause();
      }

      video.muted = isVideoMuted;
    }
  }, [isVideoPlayed, isVideoMuted]);

  useEffect(() => {
    return () => {
      if (clickTimeoutRef.current !== null) {
        clearTimeout(clickTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isVideoPlayed) {
      hasCalledOnVideoEndRef.current = false;
    }
  }, [isVideoPlayed]);

  return (
    <div
      ref={dragRef}
      className={`module-video-container ${isMiniPlayer ? "mini-player" : ""}`}
      style={{
        top: isMiniPlayer ? `${position.y}px` : "",
        left: isMiniPlayer ? `${position.x}px` : "",
        cursor: isMiniPlayer
          ? isDragging
            ? "grabbing"
            : "default"
          : "default",
      }}
      onMouseDown={handleMouseDown}
    >
      <video
        ref={videoPlayerRef}
        className="module-video-player videoPlayer"
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={() => {
          setIsVideoPlayed(false);
          setCurrentTime(0);
          // onVideoComplete();
          if (onVideoEnd) onVideoEnd();
        }}
        onClick={handleVideoClick}
        onDoubleClick={handleVideoDoubleClick}
      >
        <source src={videoSrc} type="video/mp4" />
      </video>
      <div
        className="settings-menu settingsMenu"
        style={{ display: isSettingShown ? "block" : "none" }}
      >
        <div className="settings-header">
          <span className="text-body-b2">Settings</span>
          <img
            onClick={handleSettingClick}
            src="/icons/close-btn.svg"
            className="closeSettings"
            alt="Close"
          />
        </div>
        <div className="settings-options">
          <div className="settings-option">
            <span className="text-body-b3">Playback Speed</span>
            <select
              className="text-body-b3 playbackSpeed"
              onChange={handlePlaybackSpeedChange}
            >
              <option value="0.5">0.5x</option>
              <option value="1" selected>
                Normal
              </option>
              <option value="1.5">1.5x</option>
              <option value="2">2x</option>
            </select>
          </div>
          <div className="settings-option">
            <span className="text-body-b3">Quality</span>
            <select className="videoQuality">
              <option value="auto" selected>
                Auto
              </option>
              <option value="1080p">1080p</option>
              <option value="720p">720p</option>
              <option value="480p">480p</option>
            </select>
          </div>
        </div>
      </div>
      <div className="module-video-controls">
        <div className="d-flex align-items-center">
          <div className="current-time currentTime text-body-b4">
            {formatTime(currentTime)}
          </div>
          <div
            ref={ProgressContainerRef}
            className="video-progress-bar progressBar"
            onClick={handleProgressBarClick}
          >
            <div
              ref={progressRef}
              className="video-progressed progressFill"
            ></div>
          </div>
          <div className="total-time totalTime text-body-b4">
            {formatTime(totalTime)}
          </div>
        </div>
        <div className="d-flex justify-content-between align-items-center">
          <div className="video-controls-button">
            <img
              alt="image-alt"
              onClick={handlePlayClick}
              className="playButton"
              src={
                isVideoPlayed
                  ? "/icons/video-pause-icon.svg"
                  : "/icons/video-play-icon.svg"
              }
            />
            <img
              alt="image-alt"
              onClick={handleBackwardClick}
              className="seekBackward"
              src="/icons/video-reverse-icon.svg"
            />
            <img
              alt="image-alt"
              onClick={handleForwardClick}
              className="seekForward"
              src="/icons/video-forward-icon.svg"
            />
            <img
              onClick={handleMuteClick}
              alt="image-alt"
              className="volumeButton"
              src={
                isVideoMuted
                  ? "/icons/volume-muted-icon.svg"
                  : "/icons/volume-icon.svg"
              }
            />
          </div>

          <div className="video-controls-button">
            <img
              onClick={handleSettingClick}
              alt="image-alt"
              className="settingsButton"
              src="/icons/video-setting-icon.svg"
            />
            <img
              onClick={handleMiniPlayerClick}
              alt="image-alt"
              className="miniPlayerButton"
              src="/icons/video-miniplayer-icon.svg"
            />
            <img
              onClick={handleFullScreenClick}
              alt="image-alt"
              className="fullscreenButton"
              src="/icons/video-fullscreen.svg"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

const formatTime = (time) => {
  if (isNaN(time) || time === null || time === undefined) return "00:00";
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
    2,
    "0"
  )}`;
};
